import {IShopRating, IShopRatingResult, IShopRatingSummary, RatingsFactory} from "@connox/typescript-library";
import React, {Component, createRef, useRef} from "react";
import {WithTranslation, withTranslation} from "react-i18next";
import ShopRatingComments from "./ShopRatingComments/ShopRatingComments";
import ShopRatingSummary from "./ShopRatingSummary/ShopRatingSummary";

interface IShopRatingSectionProps extends WithTranslation {
  itemIds: number[];
  shopId: number;
  initialCommentsCount: number;
  loadMoreCommentsCount: number;
}

interface IShopRatingSectionState {
  summary?: IShopRatingSummary;
  sum: number;
  comments?: IShopRating[];
  buttonDisabled: boolean;
}

class ShopRatingSection extends Component<IShopRatingSectionProps, IShopRatingSectionState> {
  private isCurrentlyLoading: boolean = false;
  private headlineDiv: React.RefObject<HTMLDivElement>;

  constructor(props: IShopRatingSectionProps) {
    super(props);
    this.state = {
      buttonDisabled: false,
      comments: [],
      sum: 0,
    };

    this.headlineDiv = createRef<HTMLDivElement>();
    this.loadMoreComments = this.loadMoreComments.bind(this);
    this.handleRatingsClick = this.handleRatingsClick.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
  }

  public async componentDidMount(): Promise<void> {
    const ratingsButtons = document.querySelectorAll(".ga-go-to-ratings");
    if (ratingsButtons.length) {
      ratingsButtons.forEach((ratingsButton) => {
        ratingsButton.addEventListener("click", this.handleRatingsClick);
      });
    }
    await this.updateComments(this.props.initialCommentsCount, 0, true);
  }

  public handleRatingsClick() {
    this.headlineDiv.current?.parentElement?.classList.add("active");
  }

  public handleToggleClick(evt: React.MouseEvent<HTMLDivElement>): void {
    const parent = evt.currentTarget.parentElement!;
    parent.classList.toggle("active");
    if (!parent.classList.contains("active")) {
      const accTop = parent.parentElement!.getBoundingClientRect().y;
      if (accTop < 0) {
        window.scroll(0, window.scrollY + accTop);
      }
    }
  }

  // Load more comments
  public async loadMoreComments() {
    if (this.isCurrentlyLoading) {
      return;
    }
    const {sum, comments} = this.state;

    // Checking if there is more to load
    if (comments && comments.length >= sum) {
      return;
    }
    this.isCurrentlyLoading = true;
    await this.updateComments(
      this.props.loadMoreCommentsCount,
      this.state.comments ? this.state.comments.length : 0,
      false,
    );
    this.isCurrentlyLoading = false;
  }

  // Function to update the comments because DRY
  public async updateComments(limit: number, offset: number, summary: boolean): Promise<void> {
    const req = {
      itemIds: this.props.itemIds,
      limit,
      offset,
      summary,
    };
    const data = await RatingsFactory.getRatings(this.props.shopId, {...req});
    if (!data) {
      return;
    }

    if (data && data.summary !== undefined) {
      this.initializeComments(data);

      return;
    }

    const commentsLoaded = data ? data.ratings : [];
    const newComments = this.state.comments && this.state.comments.concat(commentsLoaded);

    const allCommentsLoaded = (newComments && newComments.length >= this.state.sum) || commentsLoaded.length !== limit;
    this.setState({
      buttonDisabled: allCommentsLoaded,
      comments: newComments,
    });
  }

  public render() {
    const {summary, buttonDisabled} = this.state;
    const {t} = this.props;
    if (!summary) {
      return null;
    }

    return (
      <>
        <div
          ref={this.headlineDiv}
          className="accordion__item__headline"
          onClick={this.handleToggleClick}
        >
          {t("Bewertungen")}&nbsp;
          <ShopRatingSummary summary={summary} showStats={false} />
        </div>
        <div className="accordion__item__content">
          <section>
            <div>
              <h2 id={t("bewertungen")}>{t("Bewertungen")}</h2>
              <ShopRatingSummary summary={summary} showStats={true} />
              <ShopRatingComments
                loadMoreClicked={this.loadMoreComments}
                comments={this.state.comments ? this.state.comments : []}
                buttonDisabled={buttonDisabled}
              />
            </div>
          </section>
        </div>
      </>
    );
  }

  private initializeComments(data: IShopRatingResult) {
    if (!data) {
      return;
    }

    const stars = data.summary;
    let sum: number = 0;
    for (const key in stars) {
      if (stars.hasOwnProperty(key)) {
        sum += stars[key];
      }
    }

    this.setState({
      buttonDisabled: sum <= 5,
      comments: data.ratings,
      sum,
      summary: stars,
    });
  }
}

export default withTranslation()(ShopRatingSection);
